import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Download,
  Flame,
  Hash,
  Loader2,
  MessageSquare,
  RefreshCw,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { LiveSession, SentimentAnalysisResult } from '@/types';
import {
  getSentimentAnalysis,
  subscribeToSentimentAnalysis,
  triggerSentimentAnalysis,
} from '@/lib/api';
import { cn } from '@/lib/utils';

interface SentimentAnalysisProps {
  session: LiveSession;
}

export default function SentimentAnalysis({ session }: SentimentAnalysisProps) {
  const [analysis, setAnalysis] = useState<SentimentAnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAnalysis = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getSentimentAnalysis(session.id);
      setAnalysis(data);
      setIsAnalyzing(data?.status === 'processing');
    } catch (loadError) {
      console.error('加载分析结果失败:', loadError);
      setError('分析结果暂时无法加载');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadAnalysis();
    const channel = subscribeToSentimentAnalysis(session.id, (updated) => {
      setAnalysis(updated);
      if (updated.status === 'completed' || updated.status === 'failed') setIsAnalyzing(false);
    });
    return () => {
      channel.unsubscribe();
    };
  }, [session.id]);

  const handleStartAnalysis = async () => {
    try {
      setIsAnalyzing(true);
      setError(null);
      const result = await triggerSentimentAnalysis(session.id);
      if (!result.success) {
        setError(result.error || result.message);
        setIsAnalyzing(false);
        return;
      }
      if (result.status === 'completed') {
        await loadAnalysis();
        setIsAnalyzing(false);
      }
    } catch (triggerError) {
      console.error('触发分析失败:', triggerError);
      setError('触发分析失败，请稍后重试');
      setIsAnalyzing(false);
    }
  };

  const downloadAnalysis = () => {
    if (!analysis) return;
    const blob = new Blob([JSON.stringify(analysis, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sentiment-' + session.id + '.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="console-panel flex min-h-64 items-center justify-center gap-3 text-sm text-[var(--text-muted)]">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--action-blue)]" />正在读取分析结果
      </div>
    );
  }

  if (!analysis || analysis.status === 'pending') {
    return (
      <section className="console-panel flex min-h-[320px] flex-col items-center justify-center px-6 py-10 text-center">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-dashed border-[var(--border-control)] text-[var(--border-strong)]"><Sparkles className="h-5 w-5" /></div>
        <h2 className="mt-4 text-base font-bold text-[var(--text-soft)]">本场次还没有舆情分析</h2>
        <p className="mt-2 max-w-lg text-sm leading-6 text-[var(--text-muted)]">分析会生成情感分布、热门关键词、话题聚类、热度趋势和高光时刻。</p>
        <Button type="button" onClick={() => void handleStartAnalysis()} disabled={isAnalyzing || session.danmaku_count === 0} className="mt-5 min-h-11">
          {isAnalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {isAnalyzing ? '正在创建任务' : '开始分析'}
        </Button>
        {session.danmaku_count === 0 && <p className="mt-2 text-xs text-[var(--status-warning)]">当前没有弹幕数据，暂时不能开始分析。</p>}
        {error && <div role="alert" className="console-alert-error mt-4 px-4 py-3 text-sm">{error}</div>}
      </section>
    );
  }

  if (analysis.status === 'processing' || isAnalyzing) {
    return (
      <section className="console-panel">
        <div className="console-panel-header">
          <div><p className="console-kicker">ANALYSIS · 舆情分析</p><h2 className="mt-1 text-sm font-bold text-[var(--text-ink)]">分析任务运行中</h2></div>
          <span className="flex items-center gap-2 text-sm font-bold text-[var(--status-action)]"><span className="h-2 w-2 rounded-full bg-[var(--status-action)]" />分析中</span>
        </div>
        <div className="flex min-h-64 flex-col items-center justify-center px-6 py-10 text-center">
          <Loader2 className="h-9 w-9 animate-spin text-[var(--status-action)]" />
          <p className="mt-4 text-sm font-semibold text-[var(--text-soft)]">正在处理本场弹幕</p>
          <p className="mt-1 font-mono text-xs text-[var(--text-muted)]">
            {analysis.danmaku_count ? '当前结果已包含 ' + analysis.danmaku_count.toLocaleString() + ' 条' : '等待分析服务返回进度'}
          </p>
          <p className="mt-3 max-w-md text-xs leading-5 text-[var(--text-muted)]">完成后页面会通过实时订阅自动更新，不需要手动刷新。</p>
        </div>
      </section>
    );
  }

  if (analysis.status === 'failed') {
    return (
      <section className="console-alert-error flex min-h-[300px] flex-col items-center justify-center px-6 py-10 text-center">
        <AlertCircle className="h-9 w-9" />
        <h2 className="mt-4 text-base font-bold">分析失败</h2>
        <p className="mt-2 max-w-lg text-sm leading-6">{analysis.error_message || '分析服务没有返回可用结果。'}</p>
        <Button type="button" variant="outline" onClick={() => void handleStartAnalysis()} disabled={isAnalyzing} className="mt-5 border-[var(--danger-border)]">
          <RefreshCw className="h-4 w-4" />重新分析
        </Button>
        {error && <p className="mt-3 text-xs">{error}</p>}
      </section>
    );
  }

  const {
    sentiment_distribution: distribution,
    keywords,
    topic_clusters: topicClusters,
    highlight_moments: highlightMoments,
    heatmap,
  } = analysis;
  const total = distribution ? distribution.positive + distribution.neutral + distribution.negative : 0;
  const positivePercent = total ? Math.round(((distribution?.positive || 0) / total) * 100) : 0;
  const neutralPercent = total ? Math.round(((distribution?.neutral || 0) / total) * 100) : 0;
  const negativePercent = total ? Math.max(0, 100 - positivePercent - neutralPercent) : 0;
  const totalMinutes = heatmap?.length ? Math.max(...heatmap.map((point) => point.minute), 1) : 1;

  return (
    <div className="space-y-5">
      <section className="console-panel">
        <div className="console-panel-header">
          <div>
            <p className="console-kicker">ANALYSIS · 舆情分析</p>
            <h2 className="mt-1 text-sm font-bold text-[var(--text-ink)]">完整分析结果</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={downloadAnalysis}><Download className="h-4 w-4" />下载结果</Button>
            <Button type="button" size="sm" onClick={() => void handleStartAnalysis()} disabled={isAnalyzing}><RefreshCw className="h-4 w-4" />重新分析</Button>
          </div>
        </div>

        <div className="grid gap-px bg-[var(--border-subtle)] md:grid-cols-[1.25fr_1fr_1fr]">
          <div className="bg-[var(--panel-white)] p-4 md:p-[18px]">
            <p className="flex items-center gap-2 text-xs text-[var(--text-muted)]"><TrendingUp className="h-4 w-4" />情感分布</p>
            <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-[var(--border-subtle)]">
              <span className="bg-[var(--status-healthy)]" style={{ width: positivePercent + '%' }} />
              <span className="bg-[var(--status-unknown)]" style={{ width: neutralPercent + '%' }} />
              <span className="bg-[var(--status-live)]" style={{ width: negativePercent + '%' }} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[11px]">
              <span className="text-[var(--status-healthy)]">积极 {positivePercent}%</span>
              <span className="text-[var(--status-unknown)]">中性 {neutralPercent}%</span>
              <span className="text-right text-[var(--status-live)]">消极 {negativePercent}%</span>
            </div>
          </div>
          <div className="bg-[var(--panel-white)] p-4 md:p-[18px]">
            <p className="flex items-center gap-2 text-xs text-[var(--text-muted)]"><MessageSquare className="h-4 w-4" />分析弹幕数</p>
            <p className="mt-3 font-mono text-2xl font-semibold text-[var(--text-ink)]">{(analysis.danmaku_count || session.danmaku_count).toLocaleString()}</p>
            <p className="mt-1 font-mono text-[10px] text-[var(--text-muted)]">更新于 {new Date(analysis.updated_at).toLocaleString('zh-CN')}</p>
          </div>
          <div className="bg-[var(--panel-white)] p-4 md:p-[18px]">
            <p className="flex items-center gap-2 text-xs text-[var(--text-muted)]"><Flame className="h-4 w-4" />高光时刻</p>
            <p className="mt-3 font-mono text-2xl font-semibold text-[var(--text-ink)]">{highlightMoments?.length || 0}</p>
            <p className="mt-1 text-[10px] text-[var(--text-muted)]">个值得关注的时间节点</p>
          </div>
        </div>
      </section>

      {keywords && keywords.length > 0 && (
        <section className="console-panel p-4 md:p-[18px]">
          <div className="flex items-center justify-between">
            <div><p className="console-kicker">KEYWORDS · 热门关键词</p><h2 className="mt-1 text-sm font-bold text-[var(--text-ink)]">讨论焦点</h2></div>
            <Hash className="h-4 w-4 text-[var(--text-muted)]" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {keywords.slice(0, 20).map((keyword, index) => (
              <span
                key={keyword.word + index}
                className={cn(
                  'inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs',
                  keyword.sentiment === 'positive'
                    ? 'border-[var(--alert-success-border)] bg-[var(--alert-success-bg)] text-[var(--alert-success-text)]'
                    : keyword.sentiment === 'negative'
                      ? 'border-[var(--alert-error-border)] bg-[var(--alert-error-bg)] text-[var(--alert-error-text)]'
                      : 'border-[var(--border-control)] bg-[var(--panel-deep)] text-[var(--text-soft)]'
                )}
              >
                <span>{keyword.word}</span><span className="font-mono opacity-70">×{keyword.count}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      {heatmap && heatmap.length > 0 && (
        <section className="console-panel p-4 md:p-[18px]">
          <div><p className="console-kicker">HEATMAP · 弹幕热度</p><h2 className="mt-1 text-sm font-bold text-[var(--text-ink)]">热度趋势与高光位置</h2></div>
          <div className="mt-5 overflow-x-auto">
            <div className="min-w-[620px]">
              <div className="relative h-6">
                {highlightMoments?.map((moment, index) => (
                  <button
                    key={moment.minute + '-' + index}
                    type="button"
                    className={cn(
                      'absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[var(--panel-white)]',
                      moment.type === 'peak' ? 'bg-[var(--status-warning)]' : moment.type === 'sentiment_shift' ? 'bg-[var(--status-purple)]' : 'bg-[var(--status-action)]'
                    )}
                    style={{ left: (moment.minute / totalMinutes) * 100 + '%' }}
                    onClick={() => {
                      const element = document.getElementById('highlight-' + moment.minute);
                      element?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
                    }}
                    aria-label={'跳转到第 ' + moment.minute + ' 分钟高光'}
                  />
                ))}
              </div>
              <div className="flex h-28 items-end gap-1 border-b border-[var(--border-control)]">
                {heatmap.map((point, index) => {
                  const height = Math.max(3, Math.min(100, point.intensity <= 1 ? point.intensity * 100 : point.intensity));
                  return <div key={point.minute + '-' + index} className="min-w-[3px] flex-1 rounded-t-sm bg-[var(--status-action)]/75 hover:bg-[var(--action-blue)]" style={{ height: height + '%' }} title={'第 ' + point.minute + ' 分钟 · ' + point.danmaku_count + ' 条弹幕'} />;
                })}
              </div>
              <div className="mt-2 flex justify-between font-mono text-[10px] text-[var(--text-muted)]"><span>0 分钟</span><span>{totalMinutes} 分钟</span></div>
            </div>
          </div>
        </section>
      )}

      {topicClusters && topicClusters.length > 0 && (
        <section className="console-panel p-4 md:p-[18px]">
          <div><p className="console-kicker">TOPICS · 话题聚类</p><h2 className="mt-1 text-sm font-bold text-[var(--text-ink)]">主要讨论主题</h2></div>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {topicClusters.map((cluster, index) => (
              <article key={cluster.topic + index} className="rounded-md border border-[var(--border-subtle)] bg-[var(--panel-deep)] p-4">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-sm font-semibold text-[var(--text-soft)]">{cluster.topic}</h3>
                  <span className="shrink-0 font-mono text-[11px] text-[var(--text-muted)]">{cluster.count} 条</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {cluster.keywords.map((keyword, keywordIndex) => <span key={keyword + keywordIndex} className="rounded border border-[var(--status-action-border)] px-2 py-1 text-[10px] text-[var(--status-action)]">{keyword}</span>)}
                </div>
                <div className="mt-3 space-y-1.5">
                  {cluster.sample_messages.slice(0, 3).map((message, messageIndex) => <p key={messageIndex} className="truncate text-xs text-[var(--text-muted)]">“{message}”</p>)}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {highlightMoments && highlightMoments.length > 0 && (
        <section className="console-panel p-4 md:p-[18px]">
          <div><p className="console-kicker">HIGHLIGHTS · 高光时刻</p><h2 className="mt-1 text-sm font-bold text-[var(--text-ink)]">需要关注的变化</h2></div>
          <div className="mt-4 divide-y divide-[var(--border-subtle)]">
            {highlightMoments.map((moment, index) => (
              <article key={moment.minute + '-' + index} id={'highlight-' + moment.minute} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[var(--structural-border)] bg-[var(--panel-deep)]">
                  {moment.type === 'peak' ? <TrendingUp className="h-4 w-4 text-[var(--status-warning)]" /> : moment.type === 'sentiment_shift' ? <AlertCircle className="h-4 w-4 text-[var(--status-purple)]" /> : <MessageSquare className="h-4 w-4 text-[var(--status-action)]" />}
                </div>
                <div className="min-w-0"><p className="font-mono text-[11px] text-[var(--action-blue)]">第 {moment.minute} 分钟</p><p className="mt-1 text-sm leading-6 text-[var(--text-soft)]">{moment.description}</p></div>
              </article>
            ))}
          </div>
        </section>
      )}

      {error && <div role="alert" className="console-alert-error px-4 py-3 text-sm">{error}</div>}
    </div>
  );
}
